import { type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/** Mirrors `ComplianceTone` from `@sitewise/shared` — keeping this list
 *  copied here keeps the UI package free of a shared-package dep on the
 *  badge component. Validate via the typecheck in the consuming page. */
export type ComplianceTone = 'critical' | 'high' | 'medium' | 'success' | 'muted';

interface ComplianceBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone: ComplianceTone;
  /** Label to display (e.g. "Overdue", "Due soon", "Logged"). */
  children: React.ReactNode;
  /** Show a small dot to the left of the label. */
  withDot?: boolean;
}

const TONE_CLASS: Record<ComplianceTone, string> = {
  critical: 'bg-sev-critical-subtle text-sev-critical',
  high: 'bg-sev-high-subtle text-sev-high',
  medium: 'bg-sev-medium-subtle text-[#8a6808]',
  success: 'bg-success-subtle text-success',
  muted: 'bg-surface-muted text-fg-muted',
};

const DOT_CLASS: Record<ComplianceTone, string> = {
  critical: 'bg-sev-critical',
  high: 'bg-sev-high',
  medium: 'bg-sev-medium',
  success: 'bg-success',
  muted: 'bg-fg-muted',
};

/**
 * Tinted pill for compliance-record statuses. Tone is computed by
 * `complianceTone` from `@sitewise/shared` (status + dueAt + grace).
 * Visual treatment mirrors the canonical severity colors in
 * DESIGN_SYSTEM §9 so users learn one palette.
 */
export function ComplianceBadge({
  tone,
  children,
  withDot,
  className,
  ...props
}: ComplianceBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold',
        TONE_CLASS[tone],
        className,
      )}
      {...props}
    >
      {withDot ? (
        <span
          className={cn('h-1.5 w-1.5 rounded-full', DOT_CLASS[tone])}
          aria-hidden
        />
      ) : null}
      {children}
    </span>
  );
}
